/**
 * Shared form renderer used by all preview + fill surfaces (form builder preview
 * dialogs, form library preview modal, full-page /forms/preview/:id, and the
 * public intake page).
 *
 * Renders a FormFieldSchema[] grouped into sections with proper input
 * controls for every supported field_type. Designed for design fidelity
 * with FormInstanceEditor's live editor — both rely on fieldOptions.ts
 * for options normalization.
 *
 * Theming: pass an optional `theme` (a form design template). When present the
 * renderer applies the org's `--wf-*` CSS variables so inputs / labels /
 * sections take the org's colors, radius, and fonts. When omitted it renders in
 * the neutral, dark-mode-aware Tailwind look used by internal preview surfaces.
 *
 * Not a submitter: callers own value state via the optional `value` /
 * `onChange` props. Pass `disabled` to render read-only.
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { sectionAnchorId } from './FormSectionNav';

/**
 * Split a section's fields into panels at each `panel_heading` (TRUS-571).
 *
 * IFR's Member Snapshot is one section holding 9 panel headings and ~91
 * read-only fields. Laid out as one flat run, the panels stack: heading, its
 * fields, next heading, and so on down 50 rows. The design puts the panels
 * SIDE BY SIDE, which only became possible once the page stopped being capped
 * at 768px.
 *
 * Returns null when the section has no panel heading, so every other form keeps
 * the flat 12-column grid it has always had.
 */
function splitIntoPanels(fields: any[]): Array<{ heading: any; fields: any[] }> | null {
  if (!fields.some((f) => f?.field_type === 'panel_heading')) return null;
  const panels: Array<{ heading: any; fields: any[] }> = [];
  let current: { heading: any; fields: any[] } | null = null;
  for (const f of fields) {
    if (f?.field_type === 'panel_heading') {
      current = { heading: f, fields: [] };
      panels.push(current);
    } else if (current) {
      current.fields.push(f);
    } else {
      // Anything before the first heading keeps its own full-width panel so it
      // is not silently dropped.
      current = { heading: null, fields: [f] };
      panels.push(current);
    }
  }
  return panels;
}
import type { FormSectionSchema, FormFieldSchema } from '@/services/api/forms';
import { normalizeOptions } from './fieldOptions';
import { isFieldRequired, isFieldVisible } from './fieldVisibility';
import { renderFormElement, FieldTip, ReadOnlyValue, ReadOnlyFieldGroup } from './FormElements';
import { toDateInputValue, toDateTimeLocalInputValue } from '@/utils/dateTime';
import { useDynamicTranslation } from './dynamicTranslation';
import { themeToCssVars, hasTheme, mergeFormTheme, type PartialFormTheme } from './formTheme';
import { spanClassFor } from './fieldWidth';
import { SignatureDisplay } from './SignatureDisplay';
import { readSignatureRecord } from './signatureFields';

export interface FormRendererProps {
  sections: FormSectionSchema[];
  /** Field id → current value. Optional; omit for purely visual previews. */
  value?: Record<string, any>;
  /** Change handler. Omit to render purely visual. */
  onChange?: (fieldId: string, value: any) => void;
  /** Disable all inputs (read-only preview). */
  disabled?: boolean;
  /**
   * Field ids that stay EDITABLE even while `disabled` is set.
   *
   * A settled record is read-only as a whole, but a field the org flagged
   * `editable_after_submit` is the exception: IFR's LCMs submit the billing so the claim
   * can go out and finish the narrative afterwards (TRUS-566 #3). An explicit opt-in
   * rather than reading the flag off the field here, because `disabled` also serves the
   * BUILDER PREVIEW — and a preview must stay inert whatever a field is flagged.
   */
  unlockedFieldIds?: string[];
  /**
   * Render the org's logo above the form when the theme carries one.
   *
   * On by default, because a themed form that shows no logo does not look
   * branded — which was the whole complaint. PublicFormPage passes false: it
   * renders its own logo above this component and would otherwise show two.
   */
  brandHeader?: boolean;
  /** Banner shown above the form (e.g., "Preview mode — entries are not saved"). */
  banner?: React.ReactNode;
  /**
   * When true, renders the always-present Name / Email / Phone submitter block
   * that PublicFormPage prepends to every live public-link form. Enable on
   * any preview surface that simulates the public form experience so admins
   * see an accurate preview.
   */
  showSubmitterBlock?: boolean;
  /**
   * Org design template (theme). When set, the form takes the org's colors,
   * radius, and fonts via `--wf-*` CSS variables. Omit for the neutral look.
   */
  theme?: PartialFormTheme | null;
  /**
   * Flat `{key_name: text}` bundle of tenant-authored translations, from
   * `/api/public/forms/{token}/translations/{lang}` or
   * `/api/translations/dynamic/{lang}`. Omit on surfaces that have not fetched
   * one: labels then render as the tenant authored them, which is the behavior
   * every caller had before this existed.
   */
  dynamicTranslations?: Record<string, string> | null;
  /**
   * TRUS-763: offer "Add Signature" on an unsigned signature field of a saved
   * (read-only) record. Signing a submitted form does not amend it. Omit on
   * previews and public pages.
   */
  onAddSignature?: (fieldId: string) => void;
}

export const FormRenderer: React.FC<FormRendererProps> = ({
  sections,
  value,
  onChange,
  disabled: formDisabled,
  unlockedFieldIds,
  banner,
  showSubmitterBlock,
  theme,
  brandHeader = true,
  dynamicTranslations,
  onAddSignature,
}) => {
  const unlocked = React.useMemo(() => new Set(unlockedFieldIds || []), [unlockedFieldIds]);

  // COLLAPSIBLE SECTIONS.
  //
  // `collapsible` and `collapsed_by_default` have been on FormSectionSchema since
  // sections shipped, and FormInstanceEditor has honoured both all along. This
  // renderer ignored them — and this renderer draws the builder preview, the
  // full-page preview, the public intake page AND the submitted record. So a
  // template asking for 18 collapsed sections rendered as one flat scroll
  // everywhere except the draft editor. IFR's v2 ECM assessment is 260 fields
  // across 18 sections, 15 of them marked collapsed, which made every surface
  // used to REVIEW the form unusable while the surface used to FILL it was fine
  // (TRUS-571).
  //
  // Keyed by section_id and seeded once: re-deriving on every render would slam
  // a section shut the moment someone opened it.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => {
    const initial = new Set<string>();
    (sections || []).forEach((sec: any) => {
      if (sec?.collapsible && sec?.collapsed_by_default && sec?.section_id) {
        initial.add(sec.section_id);
      }
    });
    return initial;
  });
  const toggleSection = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const getValue = (fieldId: string) => value?.[fieldId] ?? '';
  const setValue = (fieldId: string, v: any) => onChange?.(fieldId, v);

  // Tenant-authored strings (field labels, option labels, section titles, help
  // text) resolve through the translation stack; app chrome already did. Falls
  // back to the tenant's own text, so an org with no translations registered
  // renders exactly as before. `tr`, not `t` — `t` is the merged theme below.
  const tr = useDynamicTranslation(dynamicTranslations);

  const themed = hasTheme(theme);
  const t = mergeFormTheme(theme);
  const sectionStyleMode = t.layout.section_style; // card | flat | divided

  // When themed, override colors/border/radius/bg via the `--wf-*` vars (set on
  // the wrapper). Inline style wins over the Tailwind classes (including dark:),
  // so a branded form renders in brand colors regardless of app theme.
  const inputStyle: React.CSSProperties | undefined = themed
    ? {
        backgroundColor: 'var(--wf-input-bg)',
        borderColor: 'var(--wf-border)',
        borderRadius: 'var(--wf-radius)',
        color: 'var(--wf-text)',
      }
    : undefined;
  const labelStyle: React.CSSProperties | undefined = themed ? { color: 'var(--wf-text)' } : undefined;
  const mutedStyle: React.CSSProperties | undefined = themed ? { color: 'var(--wf-muted)' } : undefined;
  const sectionStyle: React.CSSProperties | undefined = themed
    ? sectionStyleMode === 'card'
      ? {
          backgroundColor: 'var(--wf-surface)',
          borderColor: 'var(--wf-border)',
          borderRadius: 'var(--wf-radius)',
        }
      : sectionStyleMode === 'divided'
        ? {
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: '1px solid var(--wf-border)',
            borderRadius: 0,
          }
        : { backgroundColor: 'transparent', border: 'none', borderRadius: 0 }
    : undefined;

  // Read-only long-text (textarea / rich_text) render. A fixed-height disabled
  // `<textarea>` visually clips a long value to its first few rows, so a
  // reviewer opening a submitted Progress Note could only see the first 4–6
  // lines of the note body and had to scroll inside a tiny greyed box — they
  // "could not view the full note" (TRUS-437). In read-only mode render the
  // whole value as auto-height text (newlines preserved) so the entire note is
  // visible; the editable path keeps the textarea.
  const renderReadOnlyLongText = (currentValue: any) => {
    const text = currentValue == null ? '' : String(currentValue);
    return (
      <div
        style={inputStyle}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words min-h-[6rem]"
      >
        {text !== '' ? text : <span className="text-gray-400 dark:text-gray-500">—</span>}
      </div>
    );
  };

  const renderField = (field: FormFieldSchema) => {
    // `disabled` is the form-level lock; a field in `unlockedFieldIds` escapes it.
    // `validation.read_only` does not: it marks a field the form only DISPLAYS,
    // so it outranks both the form lock and any unlock. IFR's v2 ECM assessment
    // opens with a 45-row Member Snapshot of SFHP plan data (eligibility, ECM
    // status, authorisation dates) that staff must be able to read while filling
    // the form but must not be able to overwrite — corrections to plan data go
    // through SFHP Customer Service, not this textbox (TRUS-571).
    const disabled =
      (formDisabled && !unlocked.has(field.field_id)) || field.validation?.read_only === true;
    // Visibility is driven by field config (visibility_rules) via the shared
    // evaluator — same logic as the live editor and public intake page.
    if (!isFieldVisible(field, sections, value)) return null;

    const fieldId = field.field_id;
    const currentValue = getValue(fieldId);

    if (field.field_type === 'section_header') {
      return (
        <div className="mt-2 mb-1">
          <h4 className="text-gray-900 dark:text-white text-base font-semibold m-0" style={labelStyle}>
            {tr.label(field, field.label)}
          </h4>
          {field.description && (
            <p className="text-gray-600 dark:text-gray-400 text-xs mt-1 mb-0" style={mutedStyle}>
              {tr.description(field, field.description)}
            </p>
          )}
        </div>
      );
    }

    if (field.field_type === 'divider') {
      return (
        <hr
          className="my-2 border-gray-200 dark:border-gray-700"
          style={themed ? { borderColor: 'var(--wf-border)' } : undefined}
        />
      );
    }

    if (field.field_type === 'spacer') {
      return <div className="h-4" />;
    }

    // A READ-ONLY field renders as its value, not as a dead input. A disabled
    // <select> still has a chevron and still invites a click; 100 of them is what
    // made IFR's Member Snapshot look like a form that had stopped working
    // (TRUS-571). Element types are excluded — they draw themselves.
    const readOnly = (field as any).validation?.read_only === true;
    if (readOnly && !renderFormElement(field, currentValue)) {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" style={labelStyle}>
            {tr.label(field, field.label)}
            {field.help_text && (field as any).settings?.help_as_tip && (
              <FieldTip text={tr.help(field, field.help_text)} />
            )}
          </label>
          <ReadOnlyValue value={currentValue} options={field.options} />
          {field.help_text && !(field as any).settings?.help_as_tip && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" style={mutedStyle}>
              {tr.help(field, field.help_text)}
            </p>
          )}
        </div>
      );
    }

    // Presentational element types (TRUS-571 Layer 2) live in FormElements so
    // BOTH renderers mount the identical set — this renderer and
    // FormInstanceEditor had already drifted on divider/spacer/multiselect, and
    // an element in only one of them appears on a draft and vanishes on submit.
    // Returns null for anything it does not own, so the switch below is untouched.
    // Element text is tenant-authored like any label, so it goes through the
    // same translation: a Spanish survey's thank-you alert must not stay English.
    const element = renderFormElement(
      {
        ...field,
        label: field.label ? tr.label(field, field.label) : field.label,
        help_text: field.help_text ? tr.help(field, field.help_text) : field.help_text,
      },
      currentValue,
    );
    if (element) {
      return (
        <div>
          {field.label && field.field_type !== 'panel_heading' && field.field_type !== 'inline_alert' && (
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" style={labelStyle}>
              {tr.label(field, field.label)}
              {field.help_text ? <FieldTip text={tr.help(field, field.help_text)} /> : null}
            </label>
          )}
          {element}
        </div>
      );
    }

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" style={labelStyle}>
          {tr.label(field, field.label)}
          {/* Requiredness can depend on another answer, so this cannot read the flat
              `required` flag: IFR's eight billing fields are mandatory only once Encounter
              Type is Billable, and all eight carry `mandatory=false` because the condition
              lives in `required_rules`. Reading the flag meant this renderer — the one a
              SUBMITTED record goes through, which is 3,340 of IFR's 3,362 notes — never
              marked any of them (TRUS-566 #4). The live editor's draft path already used
              the shared evaluator; this is the other half of the same rule. */}
          {isFieldRequired(field, sections, value) && (
            <span className="text-red-500 ml-1" style={themed ? { color: 'var(--wf-error)' } : undefined}>
              *
            </span>
          )}
          {/* "Why we ask" as a tip rather than a line of prose — the design puts it
              on an `i` glyph beside the label (TRUS-571). Opt-in per field via
              settings.help_as_tip, because every other org's forms already show
              help_text as a paragraph below the input and would otherwise render
              it twice. When it IS a tip, the paragraph is suppressed. Keyboard
              reachable and labelled, so the guidance is never mouse-only. */}
          {field.help_text && (field as any).settings?.help_as_tip && (
            <FieldTip text={tr.help(field, field.help_text)} />
          )}
        </label>
        {field.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" style={mutedStyle}>
            {tr.description(field, field.description)}
          </p>
        )}

        {field.field_type === 'text' && (
          <input
            type="text"
            value={currentValue}
            onChange={(e) => setValue(fieldId, e.target.value)}
            placeholder={tr.placeholder(field, field.placeholder)}
            disabled={disabled}
            style={inputStyle}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
          />
        )}

        {field.field_type === 'textarea' &&
          (disabled ? (
            renderReadOnlyLongText(currentValue)
          ) : (
            <textarea
              value={currentValue}
              onChange={(e) => setValue(fieldId, e.target.value)}
              placeholder={tr.placeholder(field, field.placeholder)}
              rows={4}
              style={inputStyle}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
            />
          ))}

        {field.field_type === 'rich_text' &&
          (disabled ? (
            renderReadOnlyLongText(currentValue)
          ) : (
            <textarea
              value={currentValue}
              onChange={(e) => setValue(fieldId, e.target.value)}
              placeholder={tr.placeholder(field, field.placeholder)}
              rows={6}
              style={inputStyle}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
            />
          ))}

        {field.field_type === 'number' && (() => {
          // Negative-value guard (TRUS-106/108) — mirror the member add/edit form:
          // block the '-' key and reject negative onChange values. Honor an
          // explicit negative min (a form author can opt into negatives by
          // setting validation.min < 0).
          const allowNegative =
            typeof field.validation?.min === 'number' && field.validation.min < 0;
          return (
            <input
              type="number"
              value={currentValue}
              onKeyDown={(e) => {
                if (!allowNegative && e.key === '-') e.preventDefault();
              }}
              onChange={(e) => {
                const raw = e.target.value;
                if (allowNegative || raw === '' || (!isNaN(Number(raw)) && Number(raw) >= 0)) {
                  setValue(fieldId, raw);
                }
              }}
              placeholder={tr.placeholder(field, field.placeholder)}
              disabled={disabled}
              min={allowNegative ? field.validation?.min : 0}
              max={field.validation?.max}
              step={field.validation?.step}
              style={inputStyle}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
            />
          );
        })()}

        {field.field_type === 'date' && (
          <input
            type="date"
            value={toDateInputValue(currentValue)}
            onChange={(e) => setValue(fieldId, e.target.value)}
            disabled={disabled}
            min={field.validation?.minDate}
            max={field.validation?.maxDate}
            style={inputStyle}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
          />
        )}

        {field.field_type === 'time' && (
          <input
            type="time"
            value={currentValue}
            onChange={(e) => setValue(fieldId, e.target.value)}
            disabled={disabled}
            style={inputStyle}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
          />
        )}

        {field.field_type === 'datetime' && (
          <input
            type="datetime-local"
            value={toDateTimeLocalInputValue(currentValue)}
            onChange={(e) => setValue(fieldId, e.target.value)}
            disabled={disabled}
            style={inputStyle}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
          />
        )}

        {field.field_type === 'select' && (
          <select
            value={currentValue}
            onChange={(e) => setValue(fieldId, e.target.value)}
            disabled={disabled}
            style={inputStyle}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 disabled:bg-gray-50 disabled:text-gray-500"
          >
            <option value="">{tr.chrome('forms.select_placeholder', '-- Select --')}</option>
            {normalizeOptions(field.options).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {tr.option(field, opt.value, opt.label)}
              </option>
            ))}
          </select>
        )}

        {field.field_type === 'radio' && (
          <div className="flex flex-col gap-2">
            {normalizeOptions(field.options).map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 text-sm cursor-pointer text-gray-700 dark:text-gray-300"
                style={labelStyle}
              >
                <input
                  type="radio"
                  name={fieldId}
                  value={opt.value}
                  checked={currentValue === opt.value}
                  onChange={(e) => setValue(fieldId, e.target.value)}
                  disabled={disabled}
                  style={themed ? { accentColor: 'var(--wf-primary)' } : undefined}
                />
                <span>{tr.option(field, opt.value, opt.label)}</span>
              </label>
            ))}
          </div>
        )}

        {field.field_type === 'checkbox' && (() => {
          const opts = normalizeOptions(field.options);
          if (opts.length === 0) {
            return (
              <label
                className="flex items-center gap-2 text-sm cursor-pointer text-gray-700 dark:text-gray-300"
                style={labelStyle}
              >
                <input
                  type="checkbox"
                  checked={!!currentValue}
                  onChange={(e) => setValue(fieldId, e.target.checked)}
                  disabled={disabled}
                  style={themed ? { accentColor: 'var(--wf-primary)' } : undefined}
                />
                <span>{tr.placeholder(field, field.placeholder) || tr.chrome('common.yes', 'Yes')}</span>
              </label>
            );
          }
          const checked: string[] = Array.isArray(currentValue) ? currentValue : [];
          // "Choose up to three" (TRUS-624). Once the limit is reached the
          // unchosen boxes lock, so the answer can never exceed it; the server
          // rejects more as well.
          const maxSelections = Number((field as any).max_selections) || 0;
          const atLimit = maxSelections > 0 && checked.length >= maxSelections;
          return (
            <div className="flex flex-col gap-2">
              {opts.map((opt) => {
                const locked = atLimit && !checked.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 ${locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    style={labelStyle}
                  >
                    <input
                      type="checkbox"
                      value={opt.value}
                      checked={checked.includes(opt.value)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...checked, opt.value]
                          : checked.filter((v) => v !== opt.value);
                        setValue(fieldId, next);
                      }}
                      disabled={disabled || locked}
                      style={themed ? { accentColor: 'var(--wf-primary)' } : undefined}
                    />
                    <span>{tr.option(field, opt.value, opt.label)}</span>
                  </label>
                );
              })}
              {maxSelections > 0 && (
                <p
                  className="text-xs text-gray-500 dark:text-gray-400"
                  style={mutedStyle}
                  aria-live="polite"
                >
                  {tr.chrome('forms.selected_of_max', 'Selected')}{' '}
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {checked.length}/{maxSelections}
                  </span>
                </p>
              )}
            </div>
          );
        })()}

        {field.field_type === 'file' && (
          <div
            className="border border-dashed border-gray-300 dark:border-gray-600 rounded-md p-3 text-sm text-gray-500"
            style={themed ? { borderColor: 'var(--wf-border)' } : undefined}
          >
            File upload (available in live form)
          </div>
        )}

        {field.field_type === 'signature' &&
          (value === undefined ? (
            // A purely visual preview (builder) has no record to show.
            <div
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md h-24 flex items-center justify-center text-xs text-gray-400"
              style={themed ? { borderColor: 'var(--wf-border)' } : undefined}
            >
              Signature field
            </div>
          ) : (
            // TRUS-763: a saved record shows the stored signature and its own date.
            // This box used to read "Signature field" whatever was stored, so a
            // signed, submitted care plan looked unsigned.
            <SignatureDisplay
              value={currentValue}
              record={readSignatureRecord(value, fieldId)}
              onAdd={onAddSignature ? () => onAddSignature(fieldId) : undefined}
            />
          ))}

        {field.field_type === 'field_group' && (
          // Was a grey box reading "Repeatable field group (available in live
          // form)". On IFR's care plan that hid FIVE whole sections — Identified
          // Needs, Goals and Action Plan, Community Supports, Care Team, Care
          // Plan Reviews — from every preview and every submitted record,
          // because only FormInstanceEditor mounted the real group renderer.
          <ReadOnlyFieldGroup
            groupId={(field as any).field_group_id}
            label={field.label}
            rows={currentValue}
          />
        )}

        {field.field_type === 'billing_evaluator' && (
          <div className="border border-purple-200 dark:border-purple-700 rounded-md p-3 bg-purple-50 dark:bg-purple-900/20 text-sm text-purple-700 dark:text-purple-300">
            Billing AI Evaluator (available in live form)
          </div>
        )}

        {field.help_text && !(field as any).settings?.help_as_tip && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" style={mutedStyle}>
            {tr.help(field, field.help_text)}
          </p>
        )}
      </div>
    );
  };

  return (
    <div
      className="space-y-6"
      style={
        themed
          ? // max_width is a theme key the public page honored and nothing else
            // did. Applied here, every surface that renders a themed form gets
            // the measure the design template asked for.
            //
            // The background is painted for the same reason the logo is drawn
            // below: a design template's distinctive values are its brand
            // colour, its logo and its ground. Everything else in a typical
            // theme — #FFFFFF surface, #D1D5DB border, #111827 text — already
            // matches the app's default chrome, so a themed form that paints
            // none of those three is indistinguishable from an unthemed one.
            {
              ...themeToCssVars(theme),
              maxWidth: 'var(--wf-max-width)',
              marginInline: 'auto',
              backgroundColor: 'var(--wf-bg)',
            }
          : undefined
      }
    >
      {themed && brandHeader && t.branding.show_logo && t.branding.logo_url && (
        <div className="flex justify-center pb-2">
          <img src={t.branding.logo_url} alt="" className="h-12 object-contain" />
        </div>
      )}
      {banner}
      {showSubmitterBlock && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="sm:col-span-3 text-[11px] text-gray-400 dark:text-gray-500 italic mb-1">
            Always shown on live public-link forms — not configurable here
          </div>
          {(['Your Name', 'Email', 'Phone'] as const).map((label) => (
            <div key={label}>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1" style={mutedStyle}>
                {label}
              </label>
              <input
                type={label === 'Email' ? 'email' : label === 'Phone' ? 'tel' : 'text'}
                disabled
                placeholder={label === 'Email' ? 'email@example.com' : label === 'Phone' ? '(xxx) xxx-xxxx' : 'Full name'}
                style={inputStyle}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
              />
            </div>
          ))}
        </div>
      )}
      {(sections || []).length === 0 ? (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
          No sections added yet.
        </div>
      ) : (
        [...sections].sort((a, b) => a.order - b.order).map((section, sIdx) => {
          const sectionKey = section.section_id || `section-${sIdx}`;
          const canCollapse = !!(section as any).collapsible;
          const isCollapsed = canCollapse && collapsed.has(sectionKey);
          const fieldCount = (section.fields || []).length;
          // A section whose every field is hidden by skip logic is not a section
          // the person can do anything in — a screened-out survey respondent
          // would otherwise scroll past nine titled, empty cards (TRUS-624).
          if (
            fieldCount > 0 &&
            (section.fields || []).every((f) => !isFieldVisible(f, sections, value))
          ) {
            return null;
          }
          const heading = (
            <h3
              className="text-base font-semibold text-gray-900 dark:text-gray-100"
              style={
                themed
                  ? {
                      color: 'var(--wf-text)',
                      fontFamily: 'var(--wf-heading-font)',
                      fontWeight: 'var(--wf-heading-weight)' as any,
                    }
                  : undefined
              }
            >
              {tr.sectionTitle(sectionKey, section.title) ||
                `${tr.chrome('forms.section', 'Section')} ${sIdx + 1}`}
            </h3>
          );
          return (
          <section
            key={sectionKey}
            // Anchor for the section-navigation rail (TRUS-571). Harmless when no
            // rail is rendered: an id nothing links to costs nothing.
            id={sectionAnchorId(sectionKey)}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 scroll-mt-4 focus:outline-none"
            style={sectionStyle}
          >
            {/* A collapsible header is a BUTTON, not a clickable heading: it has to
                be reachable by keyboard and announce its state, or an LCM working
                without a mouse cannot open a section at all. */}
            {canCollapse ? (
              <button
                type="button"
                onClick={() => toggleSection(sectionKey)}
                aria-expanded={!isCollapsed}
                className={`flex w-full items-center justify-between gap-3 text-left ${isCollapsed ? '' : 'mb-1'}`}
              >
                {heading}
                <span className="flex items-center gap-2 text-xs" style={mutedStyle}>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fieldCount}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                    aria-hidden="true"
                  />
                </span>
              </button>
            ) : (
              <div className="mb-1">{heading}</div>
            )}
            {section.description && !isCollapsed && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" style={mutedStyle}>
                {tr.sectionDescription(section.section_id || `section-${sIdx}`, section.description)}
              </p>
            )}
            {!isCollapsed && (() => {
              const ordered = [...(section.fields || [])].sort((a, b) => a.order - b.order);
              const panels = splitIntoPanels(ordered);
              const gaps = themed
                ? { rowGap: 'var(--wf-row-gap)', columnGap: 'var(--wf-col-gap)' }
                : { rowGap: '1rem', columnGap: '1rem' };

              // PANELLED SECTION — the Member Snapshot. Each panel is a card and
              // the cards sit side by side, which is what the design draws and
              // what 1180px finally leaves room for. Inside a card the fields run
              // one per row: the card is already half the width, and splitting it
              // again gives a ~220px column that truncates every label.
              if (panels) {
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-2" style={gaps}>
                    {panels.map((panel, pIdx) => {
                      const rendered = panel.fields
                        .map((f, i) => {
                          const node = renderField(f);
                          return node ? (
                            <div key={f.field_id || `pf-${i}`}>{node}</div>
                          ) : null;
                        })
                        .filter(Boolean);
                      // A panel whose every field is hidden by a visibility rule
                      // leaves a titled empty card, which reads as missing data.
                      if (panel.heading && rendered.length === 0) return null;
                      return (
                        <div
                          key={panel.heading?.field_id || `panel-${pIdx}`}
                          className="rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden"
                          style={themed ? { borderColor: 'var(--wf-border)' } : undefined}
                        >
                          {panel.heading ? renderField(panel.heading) : null}
                          <div className="p-3 space-y-3">{rendered}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return (
                <div className="grid grid-cols-12" style={gaps}>
                  {ordered.map((field, fIdx) => {
                    const node = renderField(field);
                    // renderField returns null for a field hidden by its
                    // visibility rules. Wrapping that in a grid cell would leave
                    // a hole in the row and push the next field sideways, so the
                    // cell has to disappear with the field.
                    if (!node) return null;
                    return (
                      <div key={field.field_id || `field-${fIdx}`} className={spanClassFor(field)}>
                        {node}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </section>
          );
        })
      )}
    </div>
  );
};

export default FormRenderer;
